/**
 * Frequently Bought Together
 * Used by sections/static-product-frequently-bought-together.liquid
 *
 * Loads suggestions for the product (complementary first, then related as a
 * fallback) by rendering the section through Shopify's recommendations
 * endpoint, keeps "this item" in step with the variant picked in the main
 * product form, and adds the ticked items to the cart in one request.
 */
(() => {
  // Shopify money format, e.g. "€{{amount}}"
  function formatMoney(cents, format) {
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const withDelimiters = (amount, precision, thousands = ',', decimal = '.') => {
      const [whole, fraction] = (amount / 100).toFixed(precision).split('.');
      return whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`) + (fraction ? decimal + fraction : '');
    };
    const match = format.match(placeholder);
    let value;

    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      default: value = withDelimiters(cents, 2);
    }

    return format.replace(placeholder, value);
  }

  class FrequentlyBoughtTogether {
    constructor(el) {
      this.el = el;
      this.config = JSON.parse(el.querySelector('[data-fbt-config]').textContent);
      this.items = el.querySelector('[data-fbt-items]');
      this.total = el.querySelector('[data-fbt-total]');
      this.button = el.querySelector('[data-fbt-add]');
      this.status = el.querySelector('[data-fbt-status]');
      this.productForm = document.querySelector('[data-product-wrapper] form[data-product-form]');
      this.busy = false;

      this.load();
    }

    async load() {
      let suggestions = await this.fetchSuggestions('complementary');

      if (!suggestions && this.config.fallback) {
        suggestions = await this.fetchSuggestions('related');
      }

      // Nothing to suggest: leave the section hidden
      if (!suggestions) return;

      this.items.insertAdjacentHTML('beforeend', suggestions.items);

      // The CSS grid sizes the card column from the number of cards
      this.el.style.setProperty('--fbt-count', this.items.querySelectorAll('[data-fbt-item]').length);

      this.items.addEventListener('change', () => this.update());
      this.button.addEventListener('click', () => this.addToCart());

      if (this.productForm) {
        // The theme updates the form's variant input in its own change handler
        this.productForm.addEventListener('change', () => {
          setTimeout(() => {
            this.syncCurrentVariant();
            this.update();
          }, 0);
        });
      }

      this.syncCurrentVariant();
      this.update();
      this.el.hidden = false;
    }

    async fetchSuggestions(intent) {
      const params = new URLSearchParams({
        section_id: this.config.sectionId,
        product_id: this.config.productId,
        limit: 10,
        intent,
      });

      try {
        const response = await fetch(`${window.Theme.routes.product_recommendations_url}?${params}`);
        if (!response.ok) return null;

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const results = html.querySelector('[data-fbt-results]');
        if (!results || results.dataset.fbtCount === '0') return null;

        return { items: results.querySelector('[data-fbt-result-items]').innerHTML };
      } catch (error) {
        return null;
      }
    }

    // Mirror the variant picked in the main product form onto "this item"
    syncCurrentVariant() {
      const row = this.items.querySelector('[data-fbt-current]');
      const checkbox = row.querySelector('[data-fbt-checkbox]');
      const input = row.querySelector('[data-fbt-variant]');
      const note = row.querySelector('[data-fbt-note]');
      const formInput = this.productForm && this.productForm.querySelector('[name="id"]');
      const variantId = formInput ? formInput.value : input.value;
      const variant = this.config.variants[variantId];

      if (variant) input.dataset.price = variant.price;

      if (variant && variant.available) {
        input.value = variantId;
        note.hidden = true;
        checkbox.disabled = false;

        // Re-tick it if it was only unticked because no variant was available
        if (row.dataset.fbtAutoUnchecked) {
          checkbox.checked = true;
          delete row.dataset.fbtAutoUnchecked;
        }
      } else {
        if (checkbox.checked) row.dataset.fbtAutoUnchecked = 'true';
        checkbox.checked = false;
        checkbox.disabled = true;
        note.textContent = variant ? this.config.text.soldOut : this.config.text.selectVariant;
        note.hidden = false;
      }
    }

    selectedRows() {
      return [...this.items.querySelectorAll('[data-fbt-item]')]
        .filter((row) => row.querySelector('[data-fbt-checkbox]').checked);
    }

    update() {
      let total = 0;

      this.items.querySelectorAll('[data-fbt-item]').forEach((item) => {
        const checked = item.querySelector('[data-fbt-checkbox]').checked;
        const variant = item.querySelector('[data-fbt-variant]');
        const source = variant.tagName === 'SELECT' ? variant.selectedOptions[0] : variant;
        const price = parseInt(source.dataset.price, 10) || 0;

        item.querySelector('[data-fbt-price]').textContent = formatMoney(price, this.config.moneyFormat);
        item.classList.toggle('fbt__item--unselected', !checked);
        if (checked) total += price;
      });

      this.total.textContent = formatMoney(total, this.config.moneyFormat);
      this.button.disabled = this.busy || this.selectedRows().length === 0;
    }

    setStatus(message, { error = false, cartCount = null } = {}) {
      this.status.textContent = message;
      this.status.classList.toggle('fbt__status--error', error);

      if (cartCount !== null) {
        const link = document.createElement('a');
        link.href = window.Theme.routes.cart_url;
        link.textContent = this.config.text.viewCart.replace('%count%', cartCount);
        this.status.append(' ', link);
      }
    }

    async addToCart() {
      this.syncCurrentVariant();
      this.update();

      const items = this.selectedRows().map((row) => ({
        id: Number(row.querySelector('[data-fbt-variant]').value),
        quantity: 1,
      }));
      if (!items.length) return;

      this.busy = true;
      this.update();
      this.setStatus('');

      try {
        const response = await fetch(`${window.Theme.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || this.config.text.error);

        // Update the header cart count the same way the theme does
        const cart = await (await fetch(`${window.Theme.routes.cart_url}.js`)).json();
        window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));

        this.setStatus(this.config.text.added, { cartCount: cart.item_count });
      } catch (error) {
        this.setStatus(error.message || this.config.text.error, { error: true });
      } finally {
        this.busy = false;
        this.update();
      }
    }
  }

  const init = (root) => {
    root.querySelectorAll('[data-fbt]').forEach((el) => {
      if (el.dataset.fbtReady) return;
      el.dataset.fbtReady = 'true';
      new FrequentlyBoughtTogether(el); // eslint-disable-line no-new
    });
  };

  init(document);

  // Theme editor: sections are re-rendered in place
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
